<template>
  <div class="it-contact-page">
    <SideBar
      :groups="groups"
      :isSidebarOpen="isSidebarOpen"
      @toggle-sidebar="toggleSidebar"
      @close-sidebar="closeSidebar"
      @handle-logout="handleLogout"
    />
    <div class="main-content">
      <TopBar
        :source="tag"
        @toggle-sidebar="toggleSidebar"
        @selected="getSelected"
      />
      <div class="content-area">
        <Breadcrumb
          :items="[
            { text: 'Dashboard', link: '/dashboard' },
            { text: 'IT Contact List' },
          ]"
        />

        <SectionContainer>
          <SectionHeader title="IT Contact List" />

          <div class="contact-filter">
            <div class="search-container">
              <input
                type="text"
                v-model="searchQuery"
                placeholder="Search by name, title, property... (use quotes for exact match)"
                class="search-input"
              />
              <i class="material-icons search-icon">search</i>
            </div>
          </div>

          <div v-if="isLoading" class="loading-container">
            <LoadingSpinner size="medium" color="blue" />
          </div>

          <div v-else-if="filteredContacts.length === 0" class="no-results">
            <i class="material-icons">info</i>
            <p>No contacts found.</p>
          </div>
          <div v-else class="contacts-grid">
            <div
              v-for="(contact, index) in filteredContacts"
              :key="index"
              class="contact-card"
            >
              <div class="contact-photo">
                <img
                  v-if="
                    contact &&
                    contact.PhotoBig &&
                    isValidImage(contact.PhotoBig)
                  "
                  :src="contact.PhotoBig"
                  :alt="contact.Name"
                  class="contact-avatar"
                />
                <div
                  v-else
                  class="contact-initials"
                  :style="getInitialsBackground(contact ? contact.Name : '')"
                >
                  {{ getInitials(contact ? contact.Name : "") }}
                </div>
              </div>
              <div class="contact-info">
                <h3 class="contact-name">{{ contact ? contact.Name : "" }}</h3>
                <div class="contact-title">
                  {{ contact ? contact.JobTitle : "" }}
                </div>
                <div class="contact-property">
                  {{ contact ? contact.Property : "" }}
                </div>
                <div class="contact-detail">
                  <a
                    v-if="contact && contact.Email"
                    :href="'mailto:' + contact.Email"
                    class="contact-email"
                  >
                    <i class="material-icons">email</i>
                    <span class="contact-text">{{ contact.Email }}</span>
                  </a>
                  <a
                    v-if="contact && contact.Mobile"
                    :href="'tel:' + contact.Mobile"
                    class="contact-phone"
                  >
                    <i class="material-icons">phone</i>
                    <span class="contact-text">{{ contact.Mobile }}</span>
                  </a>
                  <button
                    @click="downloadVCard(contact)"
                    class="download-vcard"
                    :disabled="!contact || contact.downloading"
                    :aria-label="`Download contact information for ${
                      contact ? contact.Name : ''
                    }`"
                    title="Download contact information as VCF file"
                  >
                    <i
                      class="material-icons"
                      v-if="!contact || !contact.downloading"
                      >person_add</i
                    >
                    <i class="material-icons rotating" v-else>cached</i>
                    <span class="contact-text">{{
                      contact && contact.downloading
                        ? "Downloading..."
                        : "Download Contact"
                    }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </SectionContainer>
      </div>

      <AppFooter />
    </div>
  </div>
</template>

<script>
import axios from "axios";
import router from "../router";
import SideBar from "./SideBar.vue";
import TopBar from "./TopBar.vue";
import AppFooter from "./AppFooter.vue";
import SectionHeader from "./SectionHeader.vue";
import SectionContainer from "./SectionContainer.vue";
import Breadcrumb from "./Breadcrumb.vue";
import LoadingSpinner from "./LoadingSpinner.vue";
import config from "../config.js";
import "../assets/css/dashboard.css";

export default {
  name: "ITContactList",
  components: {
    SideBar,
    TopBar,
    AppFooter,
    SectionHeader,
    SectionContainer,
    Breadcrumb,
    LoadingSpinner,
  },
  data() {
    return {
      contacts: [],
      groups: [],
      user: [],
      tag: [],
      isLoading: true,
      searchQuery: "",
      isSidebarOpen: false,
      showSearchHelp: false,
      usercookie: $cookies.get("arch-it-portal"),
    };
  },
  computed: {
    filteredContacts() {
      if (!this.searchQuery) {
        return this.contacts;
      }

      // Ensure we have contacts to filter
      if (
        !this.contacts ||
        !Array.isArray(this.contacts) ||
        this.contacts.length === 0
      ) {
        return [];
      }

      const query = this.searchQuery.toLowerCase().trim();

      // Check if this is an exact search (using quotes)
      const isExactSearch =
        (query.startsWith('"') && query.endsWith('"')) ||
        (query.startsWith("'") && query.endsWith("'"));

      if (isExactSearch) {
        // Remove the quotes for exact matching
        const exactQuery = query
          .substring(1, query.length - 1)
          .toLowerCase()
          .trim();

        return this.contacts.filter((contact) => {
          if (!contact) return false;
          return (
            (contact.Name && contact.Name.toLowerCase() === exactQuery) ||
            (contact.Email && contact.Email.toLowerCase() === exactQuery) ||
            (contact.Mobile && contact.Mobile.toLowerCase() === exactQuery)
          );
        });
      }
      // If multiple words are entered, check if it's a full name search
      const queryWords = query.split(/\s+/);
      if (queryWords.length > 1) {
        // First try exact full name match
        const exactMatches = this.contacts.filter((contact) => {
          if (!contact || !contact.Name) return false;
          return contact.Name.toLowerCase() === query;
        });

        if (exactMatches.length > 0) {
          return exactMatches;
        }

        // Then try to match all words in the name
        const nameMatches = this.contacts.filter((contact) => {
          if (!contact || !contact.Name) return false;
          const name = contact.Name.toLowerCase();
          return queryWords.every((word) => name.includes(word));
        });

        if (nameMatches.length > 0) {
          return nameMatches;
        }
      }

      // Fall back to regular partial matching
      return this.contacts.filter((contact) => {
        if (!contact) return false;
        return (
          (contact.Name && contact.Name.toLowerCase().includes(query)) ||
          (contact.JobTitle &&
            contact.JobTitle.toLowerCase().includes(query)) ||
          (contact.Property &&
            contact.Property.toLowerCase().includes(query)) ||
          (contact.Email && contact.Email.toLowerCase().includes(query)) ||
          (contact.Mobile && contact.Mobile.toLowerCase().includes(query))
        );
      });
    },
  },
  methods: {
    getToken() {
      return this.$cookies.get("arch-it-portal") || this.usercookie;
    },    getContacts() {
      this.isLoading = true;
      const token = this.getToken();
      axios
        .get(
          config.getApiUrl("it-staff"),
          token
            ? {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            : {}
        )
        .then((response) => {
          this.contacts = response.data;
          this.isLoading = false;
        })
        .catch((error) => {
          console.error("Error fetching IT contacts:", error);
          this.isLoading = false;
        });
    },    getGroups() {
      const token = this.getToken();
      axios
        .get(
          config.getApiUrl("group"),
          token
            ? {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            : {}
        )
        .then((response) => (this.groups = response.data));
    },    getTag() {
      const token = this.getToken();
      axios
        .get(
          config.getApiUrl("get-tag"),
          token
            ? {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            : {}
        )
        .then((response) => (this.tag = response.data));
    },    getUser() {
      let params = new URLSearchParams();
      params.append("user", this.usercookie);
      const token = this.getToken();
      axios({
        method: "post",
        url: config.getApiUrl("get-user"),
        data: params,
        ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
      }).then((response) => {
        this.user = response.data;
        this.loginStatus(response.data);
      });
    },
    loginStatus(userData) {
      if (
        !userData ||
        !userData.expire ||
        Number(userData.expire) < Math.floor(Date.now() / 1000)
      ) {
        router.push({
          name: "Login",
        });
      }
    },
    handleLogout() {
      $cookies.remove("arch-it-portal");
      router.push({
        name: "Login",
      });
    },
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
      document.body.style.overflow = this.isSidebarOpen ? "hidden" : "";
    },
    closeSidebar() {
      this.isSidebarOpen = false;
      document.body.style.overflow = "";
    },
    getSelected(result) {
      window.open(result.value, "_blank");
    },
    handleResize() {
      if (window.innerWidth > 768 && this.isSidebarOpen) {
        this.closeSidebar();
      }
    },
    getInitials(name) {
      if (!name) return "??";

      const parts = name.trim().split(/\s+/);

      if (parts.length === 1) {
        // If only one name, return first two letters
        return parts[0].substring(0, 2).toUpperCase();
      }

      // Get first letter of first name and first letter of last name
      const firstInitial = parts[0].charAt(0);
      const lastInitial = parts[parts.length - 1].charAt(0);

      return (firstInitial + lastInitial).toUpperCase();
    },
    isValidImage(url) {
      if (!url) return false;

      // Valid image should start with imageresizer URL
      const hasImageResizer = url.includes("https://imageresizer");

      // Check for common default image patterns
      if (
        url.toLowerCase().includes("no-photo") ||
        url.toLowerCase().includes("default-avatar") ||
        url.toLowerCase().includes("blank-profile")
      ) {
        return false;
      }

      // Check for problematic image URL patterns
      // Handle both normal and escaped backslash formats
      if (
        url.includes("Assets/Images/") ||
        url.includes("Assets\\/Images\\/")
      ) {
        return false;
      }

      return hasImageResizer;
    },
    getInitialsBackground(name) {
      if (!name)
        return { background: "linear-gradient(135deg, #1565c0, #0d47a1)" };

      // Generate a color based on the name
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }

      // Use a palette of professional colors for the background
      const colors = [
        ["#1565C0", "#0D47A1"], // Blue
        ["#00796B", "#004D40"], // Teal
        ["#303F9F", "#1A237E"], // Indigo
        ["#0097A7", "#006064"], // Cyan
        ["#512DA8", "#311B92"], // Deep Purple
        ["#C2185B", "#880E4F"], // Pink
        ["#D32F2F", "#B71C1C"], // Red
        ["#7B1FA2", "#4A148C"], // Purple
        ["#388E3C", "#1B5E20"], // Green
        ["#FFA000", "#FF6F00"], // Amber
        ["#5D4037", "#3E2723"], // Brown
        ["#455A64", "#263238"], // Blue Grey
      ];

      const colorIndex = Math.abs(hash % colors.length);
      const [color1, color2] = colors[colorIndex];

      return {
        background: `linear-gradient(135deg, ${color1}, ${color2})`,
      };
    },
    downloadVCard(contact) {
      // Check if we have the minimum required information
      if (!contact.Email || !contact.Name) {
        this.showToast(
          "Incomplete contact information. Cannot generate vCard.",
          "error"
        );
        return;
      }

      // Show loading indicator for this specific contact
      this.$set(contact, "downloading", true);

      // Prepare request data
      const formData = new FormData();
      formData.append("name", contact.Name || "");
      formData.append("email", contact.Email || "");
      formData.append("phone", contact.Mobile || "");
      formData.append("jobtitle", contact.JobTitle || "");
      formData.append("property", contact.Property || "");

      // Get token for authorization
      const token = this.getToken();      // Make request to vCard API
      axios({
        method: "post",
        url: config.getApiUrl("vcard"),
        data: formData,
        responseType: "blob", // Important for file download
        ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
      })
        .then(async (response) => {
          // Create file name based on contact's name (sanitized)
          const fileName =
            contact.Name.replace(/[^a-z0-9]/gi, "_").toLowerCase() + ".vcf";

          // Create a blob from the response
          const blob = new Blob([response.data], { type: "text/x-vcard" });

          // Validate the vCard data
          try {
            const isValid = await this.isValidVCardData(blob);
            if (!isValid) {
              throw new Error("Invalid vCard data received");
            }

            // Create a temporary URL to the blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary link element
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);

            // Append to the document body
            document.body.appendChild(link);

            // Programmatically click the link to trigger download
            link.click();

            // Clean up by removing the link and revoking the URL
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            // Show success toast notification
            this.showToast(
              `Contact information for ${contact.Name} downloaded successfully!`,
              "success"
            );
          } catch (error) {
            throw new Error("Failed to process contact data");
          } finally {
            // Hide loading indicator
            this.$set(contact, "downloading", false);
          }
        })
        .catch((error) => {
          console.error("Error downloading vCard:", error);
          // Hide loading indicator
          this.$set(contact, "downloading", false);

          // Determine the error message based on the error
          let errorMessage =
            "Could not download contact information. Please try again later.";

          if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            if (error.response.status === 401) {
              errorMessage = "Authentication error. Please log in again.";
            } else if (error.response.status === 404) {
              errorMessage = "Contact download service not found.";
            } else if (error.response.status >= 500) {
              errorMessage = "Server error. Please try again later.";
            }
          } else if (error.request) {
            // The request was made but no response was received
            errorMessage =
              "No response from server. Please check your internet connection.";
          } else if (error.message.includes("Invalid vCard")) {
            errorMessage = "The contact data could not be processed correctly.";
          }

          // Show error message with a small delay to ensure the user notices it
          setTimeout(() => {
            this.showToast(errorMessage, "error");
          }, 300);
        });
    },
    isValidVCardData(data) {
      // Convert blob to text to check if it contains expected vCard format
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const text = reader.result;
          // Check if it contains basic vCard structure
          const isValid =
            typeof text === "string" &&
            text.includes("BEGIN:VCARD") &&
            text.includes("END:VCARD");
          resolve(isValid);
        };
        reader.onerror = () => reject(false);
        reader.readAsText(data);
      });
    },
    showToast(message, type = "info") {
      // Create toast element
      const toast = document.createElement("div");
      toast.className = `it-toast ${type}`;
      toast.innerHTML = `
        <div class="toast-content">
          <i class="material-icons">${this.getToastIcon(type)}</i>
          <span>${message}</span>
        </div>
      `;

      // Add to document
      document.body.appendChild(toast);

      // Trigger animation
      setTimeout(() => {
        toast.classList.add("show");
      }, 10);

      // Remove after timeout
      setTimeout(() => {
        toast.classList.remove("show");
        setTimeout(() => {
          document.body.removeChild(toast);
        }, 300);
      }, 3000);
    },

    getToastIcon(type) {
      switch (type) {
        case "success":
          return "check_circle";
        case "error":
          return "error";
        case "warning":
          return "warning";
        default:
          return "info";
      }
    },
    toggleSearchHelp() {
      this.showSearchHelp = !this.showSearchHelp;
    },
  },
  created() {
    this.getUser();
    this.getGroups();
    this.getTag();
    this.getContacts();
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
};
</script>

<style scoped>
.it-contact-page {
  display: flex;
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

.contact-filter {
  margin-bottom: 20px;
  display: flex;
  justify-content: flex-end;
}

.search-container {
  position: relative;
  width: 300px;
}

.search-input {
  width: 100%;
  padding: 10px 40px 10px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.search-input:focus {
  border-color: #1565c0;
  box-shadow: 0 2px 5px rgba(21, 101, 192, 0.15);
  outline: none;
}

.search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #757575;
  font-size: 20px;
}

.contacts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
}

.contact-card {
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: row;
  padding: 20px;
  border: 1px solid #f0f0f0;
  align-items: center;
}

.contact-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  border-color: #e0e0e0;
}

.contact-photo {
  flex: 0 0 90px;
  margin-right: 20px;
}

.contact-avatar {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #f5f5f5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.contact-initials {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 28px;
  font-weight: 600;
  text-transform: uppercase;
  border: 2px solid #f5f5f5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  user-select: none;
  letter-spacing: 0.5px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
}

.contact-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0; /* Helps with text overflow */
}

.contact-name {
  margin: 0 0 6px 0;
  font-size: 1.2rem;
  color: #00205b;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contact-title {
  color: #444;
  font-size: 0.95rem;
  margin-bottom: 4px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contact-property {
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contact-detail {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.contact-email,
.contact-phone,
.download-vcard {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  text-decoration: none;
  color: #1565c0;
  transition: color 0.2s ease, transform 0.2s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contact-email:hover,
.contact-phone:hover,
.download-vcard:hover {
  color: #0d47a1;
  transform: translateX(2px);
}

.contact-email i,
.contact-phone i,
.download-vcard i {
  font-size: 16px;
  margin-right: 8px;
  color: #1565c0;
  flex-shrink: 0;
}

.contact-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.download-vcard {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  width: 100%;
  margin-top: 4px;
}

.download-vcard:focus {
  outline: none;
}

.download-vcard:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.download-vcard i {
  color: #43a047;
}

.download-vcard .rotating {
  animation: rotate 1.5s linear infinite;
  color: #2196f3;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.loading-container {
  display: flex;
  justify-content: center;
  padding: 30px;
}

.no-results {
  text-align: center;
  padding: 30px;
  color: #757575;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.no-results i {
  font-size: 48px;
  margin-bottom: 15px;
  color: #bbdefb;
}

/* Toast notifications */
.it-toast {
  position: fixed;
  bottom: -100px;
  left: 50%;
  transform: translateX(-50%);
  min-width: 300px;
  max-width: 80%;
  padding: 0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  transition: bottom 0.3s ease-out;
  overflow: hidden;
}

.it-toast.show {
  bottom: 30px;
}

.it-toast .toast-content {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  color: white;
}

.it-toast .toast-content i {
  margin-right: 12px;
  font-size: 20px;
}

.it-toast.info {
  background-color: #2196f3;
}

.it-toast.success {
  background-color: #4caf50;
}

.it-toast.error {
  background-color: #f44336;
}

.it-toast.warning {
  background-color: #ff9800;
}

@media (max-width: 992px) {
  .contacts-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
  }

  .search-container {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .contacts-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .contact-filter {
    margin-bottom: 15px;
  }

  .contact-card {
    padding: 16px;
  }

  .contact-photo {
    flex: 0 0 70px;
    margin-right: 16px;
  }
  .contact-avatar {
    width: 70px;
    height: 70px;
  }

  .contact-initials {
    width: 70px;
    height: 70px;
    font-size: 24px;
  }

  .contact-name {
    font-size: 1.1rem;
  }

  .contact-title,
  .contact-property {
    font-size: 0.85rem;
  }

  .contact-property {
    margin-bottom: 10px;
  }
}

@media (max-width: 480px) {
  .contact-card {
    padding: 12px;
  }

  .contact-photo {
    flex: 0 0 60px;
    margin-right: 14px;
  }
  .contact-avatar {
    width: 60px;
    height: 60px;
  }

  .contact-initials {
    width: 60px;
    height: 60px;
    font-size: 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ITContactList.vue